import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowRight, Loader2, RotateCcw, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/axiosClient";
import { useAuth } from "../context/AuthContext";
import { BrandMark, BRAND_PROGRAM } from "../components/Brand";
import { FeedbackCallout } from "../components/Feedback";

// Two-point quadrant grid used in combined results
const Pt = ({ pct_x, pct_y, color, letter, label, delay }) => {
  const x = Math.max(3, Math.min(97, pct_x));
  const y = Math.max(3, Math.min(97, pct_y));
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay, type: "spring", stiffness: 220, damping: 18 }}
      className="absolute z-20"
      style={{ left: `${x}%`, bottom: `${y}%`, transform: "translate(-50%, 50%)" }}
      data-testid={`couple-point-${letter.toLowerCase()}`}
    >
      <div
        className="w-7 h-7 rounded-full border-[3px] border-white shadow-md flex items-center justify-center text-[11px] font-bold text-white"
        style={{ background: color }}
      >
        {letter}
      </div>
      <div
        className="absolute left-1/2 -translate-x-1/2 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap shadow-sm border border-white/70 max-w-[8rem] truncate"
        style={{ background: "rgba(255,255,255,0.92)", color: "#2D2A26", top: "100%" }}
      >
        {label}
      </div>
    </motion.div>
  );
};

const CoupleQuadrantGrid = ({ a, b, labelA, labelB }) => {
  const cells = [
    { k: "stable",   label: "Stable but\nDisconnected",  bg: "#F7F5F0" },
    { k: "thriving", label: "Collaborative\n& Thriving", bg: "#EBF2EA" },
    { k: "strained", label: "Strained &\nAdversarial",   bg: "#F3F4F6" },
    { k: "caring",   label: "Caring but\nReactive",      bg: "#FDF5EE" },
  ];

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold mb-3">
        Higher collaboration ↑
      </div>
      <div className="relative flex">
        <div className="flex items-center justify-center w-6">
          <span className="-rotate-90 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold">
            Collaborative Conflict
          </span>
        </div>
        <div
          className="relative flex-1 aspect-square border-2 border-[#E8E2D9] rounded-2xl overflow-hidden grid grid-cols-2 grid-rows-2 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          data-testid="couple-quadrant-grid"
        >
          {cells.map((c) => (
            <div
              key={c.k}
              style={{ background: c.bg }}
              className="relative flex items-center justify-center p-3 text-center"
            >
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider leading-snug whitespace-pre-line text-[#8A8782]">
                {c.label}
              </span>
            </div>
          ))}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-[#D1C7B8]/70 pointer-events-none" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#D1C7B8]/70 pointer-events-none" />

          {/* Mixed Patterns soft zone */}
          <div
            className="absolute pointer-events-none rounded-full"
            style={{
              left: "25%",
              top: "25%",
              width: "50%",
              height: "50%",
              background:
                "radial-gradient(circle, rgba(253,251,247,0.92) 0%, rgba(253,251,247,0.65) 55%, rgba(253,251,247,0) 80%)",
            }}
            data-testid="mixed-zone-circle"
          />
          <div className="absolute pointer-events-none top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold leading-tight text-[#8A8782]">
              Mixed
              <br />
              Patterns
            </p>
          </div>

          <Pt
            pct_x={a.emotional_connection_pct}
            pct_y={a.collaborative_conflict_pct}
            color="#C86B5E"
            letter="A"
            label={labelA}
            delay={0.5}
          />
          <Pt
            pct_x={b.emotional_connection_pct}
            pct_y={b.collaborative_conflict_pct}
            color="#8A9A86"
            letter="B"
            label={labelB}
            delay={0.75}
          />
        </div>
      </div>
      <div className="flex justify-between mt-3 ml-6 text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold">
        <span>← Lower connection</span>
        <span>Higher connection →</span>
      </div>
      <div className="text-center text-[11px] uppercase tracking-[0.22em] text-[#2D2A26] font-semibold mt-2">
        Emotional Connection
      </div>
    </div>
  );
};

const CompareBar = ({ name, aPct, bPct, labelA, labelB }) => (
  <div data-testid={`couple-dimension-${name.replace(/\W+/g, "-").toLowerCase()}`}>
    <div className="flex items-baseline justify-between mb-2">
      <span className="text-sm text-[#2D2A26] font-medium">{name}</span>
      <span className="text-xs text-[#8A8782] font-medium">
        Δ {Math.abs(Math.round(aPct - bPct))}%
      </span>
    </div>
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-[#8A8782] w-12 truncate">{labelA}</span>
        <div className="flex-1 h-1.5 bg-[#E8E2D9] rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${aPct}%` }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="h-full rounded-full"
            style={{ background: "#C86B5E" }}
          />
        </div>
        <span className="text-xs text-[#C86B5E] font-semibold w-10 text-right">{Math.round(aPct)}%</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-[#8A8782] w-12 truncate">{labelB}</span>
        <div className="flex-1 h-1.5 bg-[#E8E2D9] rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${bPct}%` }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
            className="h-full rounded-full"
            style={{ background: "#8A9A86" }}
          />
        </div>
        <span className="text-xs font-semibold w-10 text-right" style={{ color: "#8A9A86" }}>
          {Math.round(bPct)}%
        </span>
      </div>
    </div>
  </div>
);

const CouplePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/couples/${id}`);
        if (active) setCouple(res.data);
      } catch (e) {
        if (active) toast.error(formatApiError(e, "Could not load this couple invite."));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const startPartnerQuiz = () => {
    if (user) {
      navigate(`/couple/${id}/take`);
    } else {
      // Save destination then send to login
      navigate("/login", { state: { from: `/couple/${id}/take` } });
    }
  };

  const downloadCouplePdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/couples/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `couple-report-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Couple's report downloaded.");
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't download the PDF."));
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C86B5E]" />
      </div>
    );
  }
  if (!couple) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#5C5A56] mb-4">Invite not found.</p>
          <Link to="/" className="text-[#C86B5E] underline">Start a new check-in</Link>
        </div>
      </div>
    );
  }

  // Partner B not yet completed → invite landing
  if (!couple.partner_b) {
    const shareUrl = window.location.href;
    return (
      <div className="min-h-screen flex flex-col" data-testid="couple-invite-page">
        <div className="border-b border-[#E8E2D9]/60">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center">
            <div className="inline-flex items-center gap-3">
              <BrandMark className="h-9 w-auto" />
              <span className="h-7 w-px bg-[#E8E2D9]" aria-hidden="true" />
              <Link
                to="/"
                className="hidden sm:inline font-serif-display text-sm text-[#2D2A26] font-semibold hover:text-[#C86B5E] transition-colors"
              >
                {BRAND_PROGRAM}
              </Link>
            </div>
          </div>
        </div>
        <main className="flex-1 flex items-center">
          <div className="max-w-3xl mx-auto px-6 py-16 md:py-24 w-full">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-8">
                <div className="w-10 h-10 rounded-full bg-[#F8EBE9] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.8} />
                </div>
                <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
                  Couple’s check-in
                </span>
              </div>

              <h1 className="font-serif-display text-3xl sm:text-5xl text-[#2D2A26] leading-[1.05] tracking-tight font-semibold">
                <span className="text-[#C86B5E]">{couple.your_label}</span> invited you to a relationship check-in.
              </h1>

              <p className="mt-6 text-lg text-[#5C5A56] leading-relaxed max-w-2xl">
                Take 5 minutes to answer 24 honest questions. When you’re done, you’ll both see
                your results side by side on the same map — no right or wrong, just a starting
                point for conversation.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  onClick={startPartnerQuiz}
                  data-testid="partner-start-quiz-button"
                  className="group inline-flex items-center gap-2 bg-[#C86B5E] text-white rounded-full px-8 py-4 font-medium hover:bg-[#B35B4E] transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
                >
                  Start my check-in
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <span className="text-sm text-[#8A8782]">Your answers stay private — only the scores are shared.</span>
              </div>

              <div className="mt-16 rounded-2xl bg-white border border-[#E8E2D9] p-6">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold mb-2">
                  Share this link
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-[#E8E2D9] bg-[#FDFBF7] text-sm text-[#5C5A56]"
                    data-testid="share-link-input"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      setCopied(true);
                      toast.success("Link copied");
                      setTimeout(() => setCopied(false), 1800);
                    }}
                    className="px-4 py-2.5 rounded-lg border-2 border-[#E8E2D9] hover:border-[#D1C7B8] text-sm font-medium bg-white text-[#2D2A26] transition-all"
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </main>
      </div>
    );
  }

  // Combined results
  const a = couple.partner_a;
  const b = couple.partner_b;
  const samePattern = a.pattern_label === b.pattern_label;

  return (
    <div className="min-h-screen" data-testid="couple-results-page">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#F8EBE9] flex items-center justify-center">
              <Users className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.8} />
            </div>
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
              Couple’s results
            </span>
          </div>

          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#2D2A26] leading-tight font-semibold">
            {couple.your_label} & {couple.partner_label}
          </h1>
          <p className="mt-3 text-sm text-[#8A8782]">
            {samePattern
              ? `You both landed in ${a.pattern_label}.`
              : `${couple.your_label}: ${a.pattern_label} · ${couple.partner_label}: ${b.pattern_label}`}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mt-12 items-start">
          <CoupleQuadrantGrid
            a={a}
            b={b}
            labelA={couple.your_label}
            labelB={couple.partner_label}
          />

          <div className="space-y-5">
            <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-3 h-3 rounded-full bg-[#C86B5E]" />
                <p className="text-sm font-semibold text-[#2D2A26]">{couple.your_label}</p>
                <span className="ml-auto text-xs text-[#8A8782] uppercase tracking-wider">{a.pattern_label}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-xl p-3 bg-[#FDF5EE]">
                  <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Connection</p>
                  <p className="font-serif-display text-2xl text-[#2D2A26] font-semibold mt-1">
                    {Math.round(a.emotional_connection_pct)}%
                  </p>
                </div>
                <div className="rounded-xl p-3 bg-[#EBF2EA]">
                  <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Collaboration</p>
                  <p className="font-serif-display text-2xl text-[#2D2A26] font-semibold mt-1">
                    {Math.round(a.collaborative_conflict_pct)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#E8E2D9] bg-white p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-3 h-3 rounded-full" style={{ background: "#8A9A86" }} />
                <p className="text-sm font-semibold text-[#2D2A26]">{couple.partner_label}</p>
                <span className="ml-auto text-xs text-[#8A8782] uppercase tracking-wider">{b.pattern_label}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="rounded-xl p-3 bg-[#FDF5EE]">
                  <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Connection</p>
                  <p className="font-serif-display text-2xl text-[#2D2A26] font-semibold mt-1">
                    {Math.round(b.emotional_connection_pct)}%
                  </p>
                </div>
                <div className="rounded-xl p-3 bg-[#EBF2EA]">
                  <p className="text-[9px] uppercase tracking-[0.15em] text-[#8A8782] font-semibold">Collaboration</p>
                  <p className="font-serif-display text-2xl text-[#2D2A26] font-semibold mt-1">
                    {Math.round(b.collaborative_conflict_pct)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2D2A26] font-medium">
            Where you see it the same — and where you don’t
          </h2>
          <p className="text-sm text-[#8A8782] mt-1">Side-by-side dimension scores.</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-7">
            {a.dimensions.map((dim, i) => (
              <CompareBar
                key={dim.key}
                name={dim.name}
                aPct={dim.percentage}
                bPct={b.dimensions[i].percentage}
                labelA={couple.your_label}
                labelB={couple.partner_label}
              />
            ))}
          </div>
        </div>

        <div className="mt-16 rounded-2xl bg-white border border-[#E8E2D9] p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-8">
            <div data-testid="couple-reflection-a">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#C86B5E] font-semibold mb-2">
                {couple.your_label}’s reflection
              </p>
              <p className="text-xs italic text-[#8A8782] mb-3">{a.match_phrase}</p>
              <p className="text-sm sm:text-base text-[#2D2A26] leading-relaxed">{a.interpretation}</p>
              {a.growth_direction && (
                <div className="mt-4 pt-4 border-t border-[#E8E2D9]">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#C86B5E] font-semibold mb-1">
                    Growth direction
                  </p>
                  <p className="text-sm text-[#2D2A26] leading-relaxed">{a.growth_direction}</p>
                </div>
              )}
            </div>
            <div data-testid="couple-reflection-b">
              <p className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: "#8A9A86" }}>
                {couple.partner_label}’s reflection
              </p>
              <p className="text-xs italic text-[#8A8782] mb-3">{b.match_phrase}</p>
              <p className="text-sm sm:text-base text-[#2D2A26] leading-relaxed">{b.interpretation}</p>
              {b.growth_direction && (
                <div className="mt-4 pt-4 border-t border-[#E8E2D9]">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-semibold mb-1" style={{ color: "#8A9A86" }}>
                    Growth direction
                  </p>
                  <p className="text-sm text-[#2D2A26] leading-relaxed">{b.growth_direction}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="couple-restart-button"
            className="inline-flex items-center gap-2 text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#D1C7B8] rounded-full px-6 py-3 font-medium text-sm bg-white transition-all"
          >
            <RotateCcw className="w-4 h-4" />
            Start a new check-in
          </Link>
          <button
            onClick={downloadCouplePdf}
            disabled={downloadingPdf}
            data-testid="couple-download-pdf-button"
            className="inline-flex items-center justify-center gap-2 bg-[#2D2A26] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1f1d1a] disabled:opacity-50 transition-all"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
            Download couple report (PDF)
          </button>
        </div>

        <FeedbackCallout />

        <p
          className="mt-16 text-xs text-[#8A8782] leading-relaxed text-center max-w-2xl mx-auto"
          data-testid="disclaimer-text"
        >
          This quiz is for reflection and education only. It is not a clinical diagnosis or a
          substitute for counseling, therapy, or professional advice.
        </p>
      </div>
    </div>
  );
};

export default CouplePage;
